import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { jwtDecode } from 'jwt-decode';

import { cloudServiceUrl, getProfile } from '../../utils/apiConfig';

type DecodedToken = {
  exp: number;
  iat?: number;
  [key: string]: any;
};

const CLOUD_BASE_URL = cloudServiceUrl;
const profile = getProfile() === 'production';
const APP_URL = profile
  ? 'business.fresherstoday.com'
  : 'staging.app.fresherstoday.com';

// Token storage utility
const getCloudToken = () => localStorage.getItem('cloudToken');

const isTokenExpired = () => {
  const token = getCloudToken();
  if (!token) return true;

  try {
    const decoded = jwtDecode<DecodedToken>(token);
    return decoded.exp < Math.floor(Date.now() / 1000);
  } catch {
    return true;
  }
};

export const cloudApi = createApi({
  reducerPath: 'cloudApi',
  baseQuery: fetchBaseQuery({
    baseUrl: CLOUD_BASE_URL,
    prepareHeaders: (headers, { endpoint, getState }) => {
      if (!endpoint.includes('auth/get/token')) {
        const token = getCloudToken();
        if (token) headers.set('Authorization', `Bearer ${token}`);
      }
      headers.set('applicationurl', APP_URL);
      return headers;
    }
  }),
  endpoints: builder => ({
    // 1. Login to cloud
    loginToCloudService: builder.mutation<string, void>({
      query: () => ({
        url: 'auth/get/token',
        method: 'POST',
        body: {
          userName: 'aWN0c3lzdGVtc0BtYWlsaW5hdG9yLmNvbQ==',
          password: 'MTIzNDU2Nzg5'
        }
      }),
      transformResponse: (response: any) => {
        const token = response?.data;
        localStorage.setItem('cloudToken', token);
        return token;
      }
    }),

    // 2. Upload file to S3
    uploadFileToS3: builder.mutation<any, File>({
      async queryFn(file, _queryApi, _extraOptions, baseQuery) {
        if (isTokenExpired()) {
          const refresh = await baseQuery({
            url: 'auth/get/token',
            method: 'POST',
            body: {
              userName: 'aWN0c3lzdGVtc0BtYWlsaW5hdG9yLmNvbQ==',
              password: 'MTIzNDU2Nzg5'
            }
          });

          if (refresh.error) return { error: refresh.error };
          const token = (refresh.data as any)?.data;
          localStorage.setItem('cloudToken', token);
        }

        const formData = new FormData();
        formData.append('file', file);

        const uploadResult = await baseQuery({
          url: 's3/upload',
          method: 'POST',
          body: formData
        });

        return uploadResult;
      }
    }),

    // 3. Get pre-signed file URL
    getFileFromS3: builder.mutation<string, string>({
      async queryFn(fileName, _queryApi, _extraOptions, baseQuery) {
        if (isTokenExpired()) {
          const refresh = await baseQuery({
            url: 'auth/get/token',
            method: 'POST',
            body: {
              userName: 'aWN0c3lzdGVtc0BtYWlsaW5hdG9yLmNvbQ==',
              password: 'MTIzNDU2Nzg5'
            }
          });

          if (refresh.error) return { error: refresh.error };
          const token = (refresh.data as any)?.data;
          localStorage.setItem('cloudToken', token);
        }

        const response = await baseQuery({
          url: 's3/generate/presigned/url',
          method: 'POST',
          body: { objectKey: fileName }
        });

        if ((response as any).data?.data) {
          return { data: (response as any).data.data };
        }

        return { error: { status: 500, data: 'Failed to get file URL' } };
      }
    })
  })
});

export const {
  useLoginToCloudServiceMutation,
  useUploadFileToS3Mutation,
  useGetFileFromS3Mutation
} = cloudApi;
